import { PayoutMethod, TransactionPaymentMethod } from '~/swagger/portal/Api';
import { formatKnownCoaLabel } from '~/utils/chartOfAccountLabel';
import type {
  BillPayProperty,
  BillSettleProperty,
  ChartOfAccountDto,
  HydratedVendorDto,
  PaymentAccountDto,
  VendorBillItemDto,
  VendorBillLineItemDto,
  VendorBillsToPayDto,
  VendorBillsToSettleDto,
} from '~/swagger/portal/Api';
import type { BillPayment, ProcessedBill } from '~/types/payVendorBills';

const buildChartOfAccountNames = (lineItems: VendorBillLineItemDto[], chartOfAccounts: ChartOfAccountDto[], t: (key: string) => string) =>
  lineItems
    .map((item) => {
      const account = chartOfAccounts.find((chart) => chart.id === item.chartOfAccountId);
      return formatKnownCoaLabel(account, t);
    })
    .filter(Boolean) as string[];

const findFirstAchAccountId = (vendor: HydratedVendorDto) =>
  vendor.paymentAccounts?.find((account: PaymentAccountDto) => Boolean(account.plaid))?.id;

export const processBillsResponse = (
  response: VendorBillsToPayDto | VendorBillsToSettleDto,
  chartOfAccounts: ChartOfAccountDto[],
  companyAccountIds: string[],
  bankAccountOptions: { value: string }[],
  t: (key: string) => string
): BillPayment[] => {
  if (!response?.properties || !Array.isArray(response.properties) || !response.vendors || !Array.isArray(response.vendors)) {
    return [];
  }

  const vendorMap = new Map<string, HydratedVendorDto>(response.vendors.map((vendor) => [vendor.id, vendor]));
  const defaultBankAccountId = bankAccountOptions.at(0)?.value;
  const defaultPaymentMethod = TransactionPaymentMethod.Check;

  const propertyBills = response.properties.flatMap((property: BillPayProperty | BillSettleProperty) =>
    (property.bills ?? [])
      .map((bill: VendorBillItemDto): BillPayment | null => {
        const vendor = bill.vendorId ? vendorMap.get(bill.vendorId) : undefined;
        if (!vendor || !property.property) return null;

        return {
          id: bill.id,
          property: {
            ...property.property,
            availableBalance: property.availableBalance,
            reserve: property.reserve,
            availableForPayment: property.availableForPayment,
            defaultBankAccountId: property.defaultBankAccountId,
          },
          paymentBankAccountId: bill.companyBill
            ? companyAccountIds[0] || defaultBankAccountId
            : property.defaultBankAccountId || defaultBankAccountId,
          receivingBankAccountId: findFirstAchAccountId(vendor),
          paymentMethod: defaultPaymentMethod,
          paymentAmount: bill.dueAmount,
          dueAmount: bill.dueAmount,
          vendor,
          paymentType: PayoutMethod.PrintCheck,
          chartOfAccountName: buildChartOfAccountNames(bill.lineItems ?? [], chartOfAccounts, t),
          memo: bill.memo ?? '',
          externalTransactionId: '',
          billDate: bill.billDate,
          dueDate: bill.dueDate,
          totalAmount: bill.totalAmount,
          vendorMemo: vendor.defaultMemo,
          companyBill: bill.companyBill ?? false,
          files: bill.files ?? [],
          excluded: true,
        };
      })
      .filter((bill): bill is BillPayment => bill !== null)
      .filter((bill) => bill.dueAmount > 0)
  );

  const companyBills = (response.companyVendorBills ?? [])
    .map((bill): BillPayment | null => {
      const vendor = bill.vendorId ? vendorMap.get(bill.vendorId) : undefined;
      if (!vendor) return null;

      return {
        id: bill.id,
        property: bill.property,
        paymentBankAccountId: companyAccountIds[0] || defaultBankAccountId,
        receivingBankAccountId: findFirstAchAccountId(vendor),
        paymentMethod: defaultPaymentMethod,
        paymentAmount: bill.dueAmount,
        dueAmount: bill.dueAmount,
        vendor,
        paymentType: PayoutMethod.PrintCheck,
        chartOfAccountName: buildChartOfAccountNames(bill.lineItems ?? [], chartOfAccounts, t),
        memo: bill.memo ?? '',
        externalTransactionId: '',
        billDate: bill.billDate,
        dueDate: bill.dueDate,
        totalAmount: bill.totalAmount,
        vendorMemo: vendor.defaultMemo,
        companyBill: bill.companyBill ?? true,
        files: bill.files ?? [],
        excluded: true,
      };
    })
    .filter((bill): bill is BillPayment => bill !== null)
    .filter((bill) => bill.dueAmount > 0);

  return [...propertyBills, ...companyBills];
};

export const buildProcessedBills = (response: VendorBillsToPayDto | VendorBillsToSettleDto): ProcessedBill[] => {
  if (!response?.properties || !Array.isArray(response.properties) || !response.vendors || !Array.isArray(response.vendors)) {
    return [];
  }

  const vendorMap = new Map<string, HydratedVendorDto>(response.vendors.map((vendor) => [vendor.id, vendor]));

  const propertyBills = response.properties.flatMap((property: BillPayProperty | BillSettleProperty) =>
    (property.bills ?? [])
      .map((bill: VendorBillItemDto): ProcessedBill | null => {
        const vendor = bill.vendorId ? vendorMap.get(bill.vendorId) : undefined;
        if (!vendor || !property.property) return null;

        return {
          ...bill,
          property: {
            ...property.property,
            availableBalance: property.availableBalance,
            reserve: property.reserve,
            availableForPayment: property.availableForPayment,
            defaultBankAccountId: property.defaultBankAccountId,
          },
          vendor,
        };
      })
      .filter((bill): bill is ProcessedBill => bill !== null)
      .filter((bill) => bill.dueAmount > 0)
  );

  const companyBills = (response.companyVendorBills ?? [])
    .map((bill): ProcessedBill | null => {
      const vendor = bill.vendorId ? vendorMap.get(bill.vendorId) : undefined;
      if (!vendor) return null;

      return {
        ...bill,
        property: bill.property
          ? {
              ...bill.property,
            }
          : undefined,
        vendor,
      };
    })
    .filter((bill): bill is ProcessedBill => bill !== null)
    .filter((bill) => bill.dueAmount > 0);

  return [...propertyBills, ...companyBills];
};
